import React, { useState } from 'react'
import {
    Box,
    Typography,
    Card,
    CardContent,
    Avatar,
    Divider,
    Table,
    TableBody,
    TableRow,
    TableCell,
    IconButton

} from "@mui/material";
import { deepPurple } from "@mui/material/colors";
import EditIcon from '@mui/icons-material/Edit';
import SwitchButton from '../../SwitchButton';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { convertToTime, convertToTimeFormat, formatDate, convertToTimeFormatuserprofile } from '../../../modules/ConvertDate';
import { baseUrl, get, patch, post } from '../../../services/api-services';
import AddProfile from './AddProfile';
import { useCommon } from '../../../context/CommonContext';
import StatusDialog from '../../../dialogs/StatusDialog';
import EditBasicInfoDialog from '../profileModel-DialogBox/EditBasicDetail';
import { StyledButton } from '../../StyledComponent';
import { useParams } from 'react-router-dom';
import Programs from './Programs';

export default function BasicInfo({ handleSectionChange, userData, fetchUserData, userEnrollment, getWeightData, weightGoalParcent, getCurrentWeight }) {
    const [openAddProfile, setOpenAddProfile] = useState(false);
    const [openStatus, setOpenStatus] = useState(false);
    const { setSnackbar } = useCommon();
    const [openEditUser, setOpenEditUser] = useState(false);
    const [preHeight, setPreHeight] = useState(0);
    const [editData, setEditData] = useState({
        dateOfBirth: '',
        height: '',
        mobile: '',
        gender: '',
        address: '',
        alternativeContact: '',
        occupation: '',
        organization: '',
        clinicalIssues: [],
        clinicalIssuesDetails: {},
        clinicalNotes: '',
        wakeUpTime: '',
    });
    const onClickStatus = () => {
        setOpenStatus(true);
    }
    const onEditClose = () => {
        setEditData({
            dateOfBirth: '',
            height: '',
            mobile: '',
            gender: '',
            address: '',
            alternativeContact: '',
            occupation: '',
            organization: '',
            clinicalIssues: [],
            clinicalIssuesDetails: {},
            clinicalNotes: '',
            wakeUpTime: '',
        })
        setOpenEditUser(false);
    }
    const onEditUser = (op) => {
        setOpenEditUser(true);
        setPreHeight(userData?.height);
        setEditData({
            dateOfBirth: userData?.dateOfBirth,
            height: userData?.height,
            mobile: userData?.mobile,
            gender: userData?.gender,
            address: userData?.address,
            alternativeContact: userData?.alternativeContact || '',
            occupation: userData?.occupation,
            organization: userData?.organization,
            clinicalIssues: [...(userData?.clinicalIssues || [])],
            clinicalIssuesDetails: { ...(userData?.clinicalIssuesDetails || {}) },
            clinicalNotes: userData?.clinicalNotes || "",
            wakeUpTime: userData?.wakeUpTime,
            ...(userData?.gender === 'Female' && {
                menstrualHistory: {
                    lastMenstrualPhase: userData?.menstrualHistory?.lastMenstrualPhase,
                    avarageCycleLength: userData?.menstrualHistory?.avarageCycleLength,
                    periodFlowLength: userData?.menstrualHistory?.periodFlowLength,
                    regular: userData?.menstrualHistory?.regular,
                    notes: userData?.menstrualHistory?.notes,
                },
            }),
        });
    }

    const handleChangeStatus = async () => {
        const response = await patch(`/Customers/${userData.id}`, { status: userData?.status === 'active' ? 'inactive' : 'active' });
        if (response?.statusCode === 200) {
            setSnackbar({
                openSnackbar: true,
                message: 'Status updated successfully',
                severity: 'success'
            })
            fetchUserData();
            setOpenStatus(false);
        } else {
            setSnackbar({
                openSnackbar: true,
                message: 'Something went wrong!',
                severity: 'error'
            })
        }
    }

    const onEditSubmit = async () => {
        try {
            // Check if the user has weight data
            if (userData?.weight) {
                const url = `/Measurements?filter={"where": {"customerId": "${userData.id}", "type": "weight"}, "order" : "recordedAt DESC"}`;
                const response = await get(url);
                const newTarget = 0;
                if (response?.data && response.data.length > 0) {
                    const startWeight = response.data.slice(-1)[0].value;
                    const currentWeight = response.data[0].value;
                    const { targetWeight } = await weightGoalParcent(
                        editData.height,
                        editData.gender,
                        editData.dateOfBirth,
                        newTarget,
                        startWeight,
                        currentWeight
                    );

                    // Update user data with weight and target weight
                    const res = await patch(`/Customers/${userData.id}`, {
                        ...editData,
                        weightGoal: targetWeight,
                    });

                    if (res?.statusCode === 200) {
                        fetchUserData();
                    } else {
                        throw new Error("Failed to update user info.");
                    }
                } else {
                    console.error("No weight data found.");
                    throw new Error("Weight data not available for this user.");
                }
            } else {
                const res = await patch(`/Customers/${userData.id}`, editData);
                if (res?.statusCode !== 200) {
                    throw new Error("Failed to update user info.");
                }
                fetchUserData();
                getCurrentWeight();
            }
            // Check if height needs to be updated
            if (editData?.height && parseFloat(editData.height) !== parseFloat(userData?.height)) {
                const heightData = {
                    type: "height",
                    value: parseFloat(editData.height),
                    unit: "Cm",
                    recordedAt: new Date().toISOString(),
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    customerId: userData?.id,
                };

                try {
                    const measurementRes = await post(`/Measurements`, heightData);
                    if (measurementRes?.statusCode === 200) {
                        setSnackbar({
                            openSnackbar: true,
                            message: "User info and height measurement updated successfully.",
                            severity: "success",
                        });
                        getCurrentWeight()
                    } else {
                        throw new Error("Failed to save height measurement.");
                    }
                } catch (error) {
                    console.error("Error while saving height measurement:", error);
                    setSnackbar({
                        openSnackbar: true,
                        message: "Error occurred while saving height measurement.",
                        severity: "error",
                    });
                }
            } else {
                setSnackbar({
                    openSnackbar: true,
                    message: "User info updated successfully.",
                    severity: "success",
                });
            }
        } catch (error) {
            console.error("Error in onEditSubmit:", error);
            setSnackbar({
                openSnackbar: true,
                message: error.message || "An error occurred while updating user info.",
                severity: "error",
            });
        }
    };

    return (
        <Card className='h-full'>
            <CardContent className="text-center p-2">
                <Box className="flex items-center justify-between gap-2">
                    <Box>
                        <Typography sx={{ cursor: 'pointer' }} color="secondary" className="accent-text pointer" variant='body2' fontWeight={600} onClick={() => handleSectionChange("programs", 2)}>
                            {userEnrollment.length > 0 ? `WeightWonder™ ${userEnrollment[0]?.invoice?.purchaseProgram?.title}` : 'Free'} {(userEnrollment.length - 1) > 0 ? '+' + (userEnrollment.length - 1) : ''}
                        </Typography>
                    </Box>
                    <Box>
                        <SwitchButton status={userData.status} onClick={onClickStatus} /><span className="capitalize"> {userData?.status}</span>
                    </Box>
                </Box>
                <Box sx={{ position: "relative", textAlign: "center" }}>
                    <Avatar
                        sx={{ width: 100, height: 100, mx: "auto", bgcolor: deepPurple[500], mb: 2 }}
                        src={userData?.image ? `${baseUrl}/Uploads/profile-pic/download/${userData.image}` : `http://apps.eulogik.com/weightwonder-web/assets/img/userProfile.png`}
                    >
                        {userData?.name && userData?.name?.charAt(0)}
                    </Avatar>
                    <IconButton
                        onClick={() => { setOpenAddProfile(true) }}
                        variant="contained"
                        size="small"
                        sx={{
                            backgroundColor: '#3c50b4',
                            color: '#ffffff',
                            "&:hover": { backgroundColor: '#303f9f' },
                            position: "absolute",
                            bottom: 10,
                            right: "calc(50% - 45px)",
                            transform: "translateX(50%)",
                            zIndex: 2,
                            boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
                        }}
                    >
                        <CameraAltIcon sx={{ color: '#ffffff', fontSize: '18px' }} />
                    </IconButton>
                </Box>
                <Typography variant="h6" fontWeight={600}>{userData.name}</Typography>
                <Typography variant="body2">
                    +91 {userData.mobile}
                </Typography>
                <Typography variant="body2">
                    {userData.email}
                </Typography>
                <Divider className="py-2" />
                <Box className="flex items-center justify-between gap-2 ">
                    <Box marginTop={2}>
                        <Typography variant="h6" fontWeight={'bold'}>
                            Basic Detail
                        </Typography>
                    </Box>
                    <Box>
                        {(userData?.dateOfBirth && userData?.height && userData?.gender) || userData?.address || userData?.occupation || userData?.organization || (userData?.clinicalIssues?.length > 0) || userData.wakeUpTime ? (
                            <IconButton size='small' onClick={() => onEditUser("update")}>
                                <EditIcon />
                            </IconButton>
                        ) : (
                            <Box textAlign="" marginTop={2}>
                                <StyledButton
                                    onClick={() => onEditUser("add")}
                                >
                                    Add Info
                                </StyledButton>
                            </Box>
                        )}
                    </Box>
                </Box>
                {(userData.dateOfBirth && userData.height && userData.gender && userData.address) || userData.occupation || userData.organization || (userData.clinicalIssues?.length > 0) || userData.wakeUpTime ? (
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ pb: 1, border: "none", color: "gray" }}>
                                    DOB
                                </TableCell>
                                <TableCell sx={{ pb: 1, border: "none", fontWeight: 600 }}>
                                    {formatDate(userData.dateOfBirth) || "-"}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pb: 1, border: "none", color: "gray" }}>
                                    Gender
                                </TableCell>
                                <TableCell sx={{ pb: 1, border: "none", fontWeight: 600 }}>{userData.gender || "-"}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pb: 1, border: "none", color: "gray" }}>
                                    Alternative Contact No.
                                </TableCell>
                                <TableCell sx={{ pb: 1, border: "none", fontWeight: 600 }}>{userData?.alternativeContact || "-"}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pb: 1, border: "none", color: "gray" }}>
                                    Height (in cms)
                                </TableCell>
                                <TableCell sx={{ pb: 1, border: "none", fontWeight: 600 }}>{userData.height ? `${userData.height} Cms` : "-"}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pb: 1, border: "none", color: "gray" }}>
                                    Address
                                </TableCell>
                                <TableCell sx={{ pb: 1, border: "none", fontWeight: 600 }}>{userData.address || "-"}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pb: 1, border: "none", color: "gray" }}>
                                    Occupation
                                </TableCell>
                                <TableCell sx={{ pb: 1, border: "none", fontWeight: 600 }}>{userData.occupation || "-"}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pb: 1, border: "none", color: "gray" }}>
                                    Organization
                                </TableCell>
                                <TableCell sx={{ pb: 1, border: "none", fontWeight: 600 }}>{userData.organization || "-"}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pb: 1, border: "none", color: "gray" }}>
                                    Wake up time
                                </TableCell>
                                <TableCell sx={{ pb: 1, border: "none", fontWeight: 600 }}>{convertToTimeFormatuserprofile(userData.wakeUpTime) || "-"}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ pb: 1, border: "none", color: "gray" }}>
                                    Clinical Issues
                                </TableCell>
                                <TableCell sx={{ pb: 1, border: "none", fontWeight: 600 }}>
                                    {userData.clinicalIssues?.length > 0
                                        ? userData.clinicalIssues.map(issue =>
                                            userData.clinicalIssuesDetails?.[issue]
                                                ? `${issue} (Medicine: ${userData.clinicalIssuesDetails[issue]})`
                                                : issue
                                        ).join(", ")
                                        : "-"}
                                </TableCell>
                            </TableRow>
                            {userData.clinicalNotes && (
                                <TableRow>
                                    <TableCell sx={{ pb: 1, border: "none", color: "gray" }}>
                                        Clinical Notes
                                    </TableCell>
                                    <TableCell sx={{ pb: 1, border: "none", fontWeight: 600 }}>
                                        {userData.clinicalNotes}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                ) : (
                    null
                )}
            </CardContent>
            <AddProfile open={openAddProfile} setOpen={setOpenAddProfile} customerId={userData?.id} fetchUserData={fetchUserData} />
            <StatusDialog open={openStatus} setOpen={setOpenStatus} onSubmit={handleChangeStatus} status={userData?.status} name="User" />
            <EditBasicInfoDialog preHeight={preHeight} open={openEditUser} onClose={onEditClose} userData={editData} setUserData={setEditData} onSubmit={onEditSubmit} getWeightData={getWeightData} />
        </Card>
    )
}
